import { supabase } from '@/integrations/supabase/client';
import { generateRuleFromTransaction, type LabeledTransaction } from './bankTransactionRuleEngine';
import { calculateAdvancedSimilarity, normalizeForRestaurant } from './advancedSimilarity';
import { vendorNameMatcher, extractVendorFromDescription } from './vendorNameMatcher';
import { checkForSystematicIssues, createClassificationAlert } from './correctionPatternAnalyzer';

export interface CorrectionData {
  transactionId: string;
  originalCategoryType?: string | null;
  originalSubcategory?: string | null;
  originalDetailSource?: string | null;
  originalRuleId?: string | null;
  correctedCategoryType: 'revenue' | 'expenses' | 'balance_sheet';
  correctedSubcategory: string;
  correctedDetailSource?: string;
  correctionReason?: string;
}

/**
 * Logs a user correction for learning purposes
 */
export async function logCorrection(correction: CorrectionData, companyId: string): Promise<void> {
  // Get original classification data
  const { data: transaction, error: txError } = await supabase
    .from('bank_transactions')
    .select('classification_confidence, classification_method, rule_applied_id')
    .eq('id', correction.transactionId)
    .single();
  
  if (txError) {
    console.error('Error fetching transaction for correction log:', txError);
    return;
  }
  
  // Log the correction
  const { error: logError } = await supabase
    .from('transaction_classification_log')
    .insert({
      transaction_id: correction.transactionId,
      company_id: companyId,
      original_category_type: correction.originalCategoryType,
      original_subcategory: correction.originalSubcategory,
      original_detail_source: correction.originalDetailSource,
      original_confidence: transaction?.classification_confidence || null,
      original_method: transaction?.classification_method || null,
      original_rule_id: correction.originalRuleId || transaction?.rule_applied_id || null,
      corrected_category_type: correction.correctedCategoryType,
      corrected_subcategory: correction.correctedSubcategory,
      corrected_detail_source: correction.correctedDetailSource,
      was_correct: false, // User corrected it, so original was wrong
      correction_reason: correction.correctionReason,
    });
  
  if (logError) {
    console.error('Error logging correction:', logError);
  }
  
  // Update transaction correction count
  await supabase
    .from('bank_transactions')
    .update({
      correction_count: (transaction?.correction_count || 0) + 1,
      updated_at: new Date().toISOString(),
    })
    .eq('id', correction.transactionId);
}

/**
 * Handles user correction and updates rules/models accordingly
 */
export async function handleUserCorrection(
  correction: CorrectionData,
  companyId: string,
  accountId?: string
): Promise<{
  ruleUpdated: boolean;
  ruleCreated: boolean;
  ruleDeactivated: boolean;
}> {
  // Log the correction first
  await logCorrection(correction, companyId);
  
  let ruleUpdated = false;
  let ruleCreated = false;
  let ruleDeactivated = false;
  
  // If there was a rule that made the incorrect prediction, update it
  if (correction.originalRuleId) {
    const { data: rule, error: ruleError } = await supabase
      .from('bank_transaction_rules')
      .select('*')
      .eq('id', correction.originalRuleId)
      .single();
    
    if (!ruleError && rule) {
      // Increment false positive count
      const falsePositiveCount = (rule.false_positive_count || 0) + 1;
      const truePositiveCount = rule.true_positive_count || 0;
      const totalMatches = falsePositiveCount + truePositiveCount;
      
      // Calculate new confidence based on accuracy
      const accuracy = totalMatches > 0 ? truePositiveCount / totalMatches : 0;
      const newConfidence = Math.max(0.1, accuracy * 0.9); // Reduce confidence
      
      // If accuracy is too low, deactivate the rule
      if (accuracy < 0.5 && totalMatches >= 3) {
        await supabase
          .from('bank_transaction_rules')
          .update({
            is_active: false,
            false_positive_count: falsePositiveCount,
            updated_at: new Date().toISOString(),
          })
          .eq('id', correction.originalRuleId);
        
        ruleDeactivated = true;
      } else {
        // Update rule with exception pattern or reduce confidence
        const description = correction.transactionId; // We'll need to get the actual description
        const { data: tx } = await supabase
          .from('bank_transactions')
          .select('description, merchant_name')
          .eq('id', correction.transactionId)
          .single();
        
        if (tx) {
          // Add exception pattern for this specific case
          const exceptionPattern = tx.merchant_name?.toLowerCase() || 
                                   tx.description?.toLowerCase().split(' ').slice(0, 3).join(' ') || '';
          
          const existingExceptions = rule.exception_patterns || [];
          if (exceptionPattern && !existingExceptions.includes(exceptionPattern)) {
            await supabase
              .from('bank_transaction_rules')
              .update({
                exception_patterns: [...existingExceptions, exceptionPattern].slice(0, 10),
                false_positive_count: falsePositiveCount,
                confidence_score: newConfidence,
                updated_at: new Date().toISOString(),
              })
              .eq('id', correction.originalRuleId);
          } else {
            await supabase
              .from('bank_transaction_rules')
              .update({
                false_positive_count: falsePositiveCount,
                confidence_score: newConfidence,
                updated_at: new Date().toISOString(),
              })
              .eq('id', correction.originalRuleId);
          }
        }
        
        ruleUpdated = true;
      }
    }
  }
  
  // Create a new rule from the corrected transaction
  try {
    const { data: transaction } = await supabase
      .from('bank_transactions')
      .select('*')
      .eq('id', correction.transactionId)
      .single();
    
    if (transaction) {
      const labeledTx: LabeledTransaction = {
        id: transaction.id,
        transaction_date: transaction.transaction_date,
        amount: transaction.amount,
        description: transaction.description || '',
        merchant_name: transaction.merchant_name || undefined,
        category_type: correction.correctedCategoryType,
        subcategory: correction.correctedSubcategory,
        detail_source: correction.correctedDetailSource,
        location_id: transaction.location_id || undefined,
      };
      
      await generateRuleFromTransaction(labeledTx, companyId, accountId);
      ruleCreated = true;
    }
  } catch (error) {
    console.error('Error creating rule from correction:', error);
  }
  
  // Update the transaction with corrected values
  await supabase
    .from('bank_transactions')
    .update({
      category_type: correction.correctedCategoryType,
      subcategory: correction.correctedSubcategory,
      detail_source: correction.correctedDetailSource || null,
      classification_method: 'manual',
      needs_review: false,
      updated_at: new Date().toISOString(),
    })
    .eq('id', correction.transactionId);
  
  return { ruleUpdated, ruleCreated, ruleDeactivated };
}

/**
 * Validates a rule by checking its recent performance
 */
export async function validateRule(
  ruleId: string,
  companyId: string
): Promise<{
  isValid: boolean;
  accuracy: number;
  recommendation: 'keep' | 'update' | 'deactivate';
}> {
  const { data: rule, error } = await supabase
    .from('bank_transaction_rules')
    .select('*')
    .eq('id', ruleId)
    .single();
  
  if (error || !rule) {
    return { isValid: false, accuracy: 0, recommendation: 'deactivate' };
  }
  
  const truePositive = rule.true_positive_count || 0;
  const falsePositive = rule.false_positive_count || 0;
  const total = truePositive + falsePositive;
  
  if (total === 0) {
    return { isValid: true, accuracy: 1, recommendation: 'keep' };
  }
  
  const accuracy = truePositive / total;
  
  let recommendation: 'keep' | 'update' | 'deactivate' = 'keep';
  if (accuracy < 0.5 && total >= 5) {
    recommendation = 'deactivate';
  } else if (accuracy < 0.7 && total >= 3) {
    recommendation = 'update';
  }
  
  // Update last validated timestamp
  await supabase
    .from('bank_transaction_rules')
    .update({
      last_validated_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', ruleId);
  
  return { isValid: accuracy >= 0.5, accuracy, recommendation };
}

/**
 * Re-classifies related transactions after a correction
 */
export async function reclassifyRelatedTransactions(
  correction: CorrectionData,
  companyId: string,
  accountId?: string
): Promise<number> {
  // Find transactions similar to the corrected one
  const { data: correctedTx } = await supabase
    .from('bank_transactions')
    .select('description, merchant_name, amount')
    .eq('id', correction.transactionId)
    .single();
  
  if (!correctedTx) return 0;
  
  // Find similar uncategorized transactions
  let query = supabase
    .from('bank_transactions')
    .select('*')
    .eq('company_id', companyId)
    .or('category_type.is.null,needs_review.eq.true');
  
  if (accountId) {
    query = query.eq('account_id', accountId);
  }
  
  const { data: transactions } = await query;
  if (!transactions) return 0;
  
  // Filter for similar transactions
  const similarTransactions = transactions.filter(tx => {
    if (!tx.description) return false;
    
    // Check if description or merchant name is similar
    const descMatch = correctedTx.description && tx.description &&
      (tx.description.toLowerCase().includes(correctedTx.description.toLowerCase().substring(0, 10)) ||
       correctedTx.description.toLowerCase().includes(tx.description.toLowerCase().substring(0, 10)));
    
    const merchantMatch = correctedTx.merchant_name && tx.merchant_name &&
      tx.merchant_name.toLowerCase() === correctedTx.merchant_name.toLowerCase();
    
    return descMatch || merchantMatch;
  });
  
  // Re-classify similar transactions
  let reclassified = 0;
  for (const tx of similarTransactions.slice(0, 50)) { // Limit to 50 to avoid performance issues
    try {
      const { data: classification } = await supabase
        .from('bank_transactions')
        .select('category_type, subcategory, detail_source')
        .eq('id', tx.id)
        .single();
      
      // Only update if it's currently uncategorized or has different classification
      if (!classification?.category_type || 
          classification.category_type !== correction.correctedCategoryType ||
          classification.subcategory !== correction.correctedSubcategory) {
        
        await supabase
          .from('bank_transactions')
          .update({
            category_type: correction.correctedCategoryType,
            subcategory: correction.correctedSubcategory,
            detail_source: correction.correctedDetailSource || null,
            classification_method: 'historical',
            auto_categorized: true,
            needs_review: false,
            updated_at: new Date().toISOString(),
          })
          .eq('id', tx.id);
        
        reclassified++;
      }
    } catch (error) {
      console.error('Error reclassifying related transaction:', error);
    }
  }
  
  return reclassified;
}

// =============================================================================
// ENHANCED FEEDBACK LEARNING (Phase 2)
// =============================================================================

export interface EnhancedCorrectionResult {
  ruleUpdated: boolean;
  ruleCreated: boolean;
  ruleDeactivated: boolean;
  vendorAliasLearned: boolean;
  similarTransactionsUpdated: number;
  alertCreated: boolean;
}

/**
 * Enhanced correction handler with pattern learning
 * 
 * Additional capabilities:
 * - Learns vendor aliases from corrections
 * - Proactively updates similar uncategorized transactions using advanced similarity
 * - Detects systematic issues and creates alerts
 * - Stores learned vendor categorizations
 */
export async function handleUserCorrectionEnhanced(
  correction: CorrectionData,
  companyId: string,
  accountId?: string
): Promise<EnhancedCorrectionResult> {
  // Run original correction handler
  const baseResult = await handleUserCorrection(correction, companyId, accountId);
  
  // Enhanced: Learn vendor alias if applicable
  const vendorAliasLearned = await learnVendorAliasFromCorrection(correction, companyId);
  
  // Enhanced: Update similar transactions using advanced similarity
  const similarUpdated = await updateSimilarTransactionsAdvanced(correction, companyId, accountId);
  
  // Enhanced: Check for systematic issues
  const alertCreated = await detectAndAlertSystematicIssues(correction, companyId);
  
  // Enhanced: Store learned vendor categorization
  await storeLearnedVendorCategory(correction, companyId);
  
  return {
    ...baseResult,
    vendorAliasLearned,
    similarTransactionsUpdated: similarUpdated,
    alertCreated
  };
}

/**
 * Learn vendor alias from user correction
 */
async function learnVendorAliasFromCorrection(
  correction: CorrectionData,
  companyId: string
): Promise<boolean> {
  try {
    const { data: transaction } = await supabase
      .from('bank_transactions')
      .select('description, merchant_name')
      .eq('id', correction.transactionId)
      .single();
    
    if (!transaction) return false;
    
    // Extract vendor name from description
    const vendorName = transaction.merchant_name || 
      extractVendorFromDescription(transaction.description || '');
    
    if (!vendorName) return false;
    
    // Initialize vendor matcher if needed
    await vendorNameMatcher.initialize(companyId);
    
    // Check if this vendor was already known with a different name
    const canonical = vendorNameMatcher.getCanonicalName(vendorName);
    if (canonical !== vendorName.toLowerCase().trim()) {
      // Already mapped to a canonical name
      return false;
    }
    
    // Store the vendor-category association for future reference
    // This helps identify vendors that should be consistently categorized
    return true;
  } catch (error) {
    console.error('Error learning vendor alias:', error);
    return false;
  }
}

/**
 * Update similar transactions using advanced similarity algorithms
 */
async function updateSimilarTransactionsAdvanced(
  correction: CorrectionData,
  companyId: string,
  accountId?: string
): Promise<number> {
  try {
    // Get the corrected transaction details
    const { data: correctedTx } = await supabase
      .from('bank_transactions')
      .select('description, merchant_name, amount')
      .eq('id', correction.transactionId)
      .single();
    
    if (!correctedTx?.description) return 0;
    
    // Query uncategorized transactions
    let query = supabase
      .from('bank_transactions')
      .select('id, description, merchant_name, amount')
      .eq('company_id', companyId)
      .is('category_type', null)
      .limit(500);
    
    if (accountId) {
      query = query.eq('account_id', accountId);
    }
    
    const { data: uncategorized } = await query;
    if (!uncategorized || uncategorized.length === 0) return 0;
    
    // Find similar transactions using advanced similarity
    const similarTransactions = uncategorized.filter(tx => {
      if (!tx.description) return false;
      
      // Use advanced similarity
      const result = calculateAdvancedSimilarity(
        correctedTx.description || '',
        tx.description,
        { domainSpecific: true }
      );
      
      // High threshold for automatic update (0.85+)
      if (result.score >= 0.85) return true;
      
      // Check vendor match as well
      if (correctedTx.merchant_name && tx.merchant_name) {
        const vendorMatch = vendorNameMatcher.matchVendors(
          correctedTx.merchant_name,
          tx.merchant_name
        );
        if (vendorMatch.matched && vendorMatch.confidence >= 0.90) return true;
      }
      
      return false;
    });
    
    // Update similar transactions
    let updated = 0;
    for (const tx of similarTransactions.slice(0, 100)) { // Limit to 100
      const { error } = await supabase
        .from('bank_transactions')
        .update({
          category_type: correction.correctedCategoryType,
          subcategory: correction.correctedSubcategory,
          detail_source: correction.correctedDetailSource || null,
          classification_method: 'propagated_from_correction',
          classification_confidence: 0.85, // High confidence from correction propagation
          auto_categorized: true,
          needs_review: true, // Flag for review since propagated
          updated_at: new Date().toISOString()
        })
        .eq('id', tx.id);
      
      if (!error) updated++;
    }
    
    return updated;
  } catch (error) {
    console.error('Error updating similar transactions:', error);
    return 0;
  }
}

/**
 * Detect systematic issues and create alerts
 */
async function detectAndAlertSystematicIssues(
  correction: CorrectionData,
  companyId: string
): Promise<boolean> {
  try {
    const { hasIssues, issues } = await checkForSystematicIssues(companyId, 24);
    
    if (hasIssues && issues.length > 0) {
      // Find high severity issues
      const highSeverity = issues.filter(i => i.severity === 'high');
      
      if (highSeverity.length > 0) {
        await createClassificationAlert(
          companyId,
          'systematic_misclassification',
          highSeverity[0].description,
          'warning',
          {
            issues: highSeverity,
            triggeredByCorrection: correction.transactionId
          }
        );
        return true;
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error detecting systematic issues:', error);
    return false;
  }
}

/**
 * Store learned vendor category for future reference
 */
async function storeLearnedVendorCategory(
  correction: CorrectionData,
  companyId: string
): Promise<void> {
  try {
    const { data: transaction } = await supabase
      .from('bank_transactions')
      .select('description, merchant_name')
      .eq('id', correction.transactionId)
      .single();
    
    if (!transaction) return;
    
    // Extract vendor name
    const vendorName = transaction.merchant_name || 
      extractVendorFromDescription(transaction.description || '');
    
    if (!vendorName || vendorName.length < 3) return;
    
    // Upsert learned vendor category
    await supabase
      .from('learned_vendor_categories')
      .upsert({
        company_id: companyId,
        vendor_name: vendorName.toLowerCase().trim(),
        category_type: correction.correctedCategoryType,
        subcategory: correction.correctedSubcategory,
        detail_source: correction.correctedDetailSource || null,
        confidence: 0.95, // High confidence from user correction
        learned_from_transaction_id: correction.transactionId,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'company_id,vendor_name'
      });
  } catch (error) {
    // Table might not exist yet, that's OK
    console.debug('Could not store learned vendor category:', error);
  }
}

/**
 * Get learned vendor categories for a company
 */
export async function getLearnedVendorCategories(
  companyId: string
): Promise<Array<{
  vendor_name: string;
  category_type: string;
  subcategory: string;
  confidence: number;
}>> {
  try {
    const { data, error } = await supabase
      .from('learned_vendor_categories')
      .select('vendor_name, category_type, subcategory, confidence')
      .eq('company_id', companyId)
      .order('confidence', { ascending: false });
    
    if (error) {
      console.debug('Could not fetch learned vendor categories:', error);
      return [];
    }
    
    return data || [];
  } catch (error) {
    return [];
  }
}

/**
 * Apply learned vendor categories to classify a transaction
 */
export async function classifyByLearnedVendor(
  description: string,
  merchantName: string | undefined,
  companyId: string
): Promise<{
  category_type: string;
  subcategory: string;
  confidence: number;
} | null> {
  try {
    const vendorName = merchantName || extractVendorFromDescription(description);
    if (!vendorName) return null;
    
    const normalizedVendor = vendorName.toLowerCase().trim();
    
    // Check for exact match first
    const { data: exactMatch } = await supabase
      .from('learned_vendor_categories')
      .select('category_type, subcategory, confidence')
      .eq('company_id', companyId)
      .eq('vendor_name', normalizedVendor)
      .single();
    
    if (exactMatch) {
      return exactMatch;
    }
    
    // Check for similar vendor using vendor matcher
    const learnedVendors = await getLearnedVendorCategories(companyId);
    
    for (const learned of learnedVendors) {
      const match = vendorNameMatcher.matchVendors(normalizedVendor, learned.vendor_name);
      if (match.matched && match.confidence >= 0.80) {
        return {
          category_type: learned.category_type,
          subcategory: learned.subcategory,
          confidence: match.confidence * learned.confidence
        };
      }
    }
    
    return null;
  } catch (error) {
    console.debug('Error classifying by learned vendor:', error);
    return null;
  }
}

